import { usePortalMessage, type PortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The workspace ARCHIVE PREFLIGHT — the item-by-item table and the dialog that confirms it (plan-014 B4; design
 * §27.15.1; FR12-WSP-03). Drawn to the prototype's "Before archiving Roma — Trastevere" table
 * (`design/WeTalk.dc.html`, the Organization `lifecycle` page): three columns — the item, **Now**, **On archive** —
 * one row each for conversations in progress, campaigns, numbers and web chat, API keys, a dedicated balance and
 * recurring add-ons, then "Archive when calls finish".
 *
 * Every word in the table is the API's (`GET /v1/workspaces/{id}/archive-preflight`), so the page shows exactly what
 * the server will do, and the confirmation stores that very table. Confirming drains: no new conversations or
 * campaign attempts; the workspace archives when the last conversation ends — at once when none is in progress. An
 * archive still draining can be cancelled (numbers reconnected; keys and campaigns stay off — said so).
 *
 * Used twice: on Workspace detail (this dialog) and on Organization → Archive and close (the table inline). Styles:
 * the Organization renderer's table (`op-table`) plus `./archive-preflight.css`, tokens only (ADR 0018).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiProblemText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import type { ArchivePreflightItemCodeView, ArchivePreflightItemView, WorkspaceLifecycleOperationView } from "../../api/slice/lifecycle.js";

import "./archive-preflight.css";

/** The row titles, in the prototype's words. */
export const PREFLIGHT_ITEM_WORD: Readonly<Record<ArchivePreflightItemCodeView, string>> = {
  get conversation_in_progress() { return portalText("core", "workspaces.conversations_in_progress_9c4b91"); },
  get campaign() { return portalText("core", "workspaces.campaigns_01a23a"); },
  get channel_binding() { return portalText("core", "workspaces.numbers_and_web_chat_0a3946"); },
  get api_key() { return portalText("core", "workspaces.api_keys_94fcf3"); },
  get dedicated_balance() { return portalText("core", "workspaces.dedicated_balance_a4e27b"); },
  get recurring_add_on() { return portalText("core", "workspaces.recurring_add_ons_be3856"); },
};

export const archivePreflightKey = (workspaceId: string) => ["workspace-lifecycle", "preflight", workspaceId] as const;

/** What the person is told once an archive is confirmed. */
export function archiveOutcomeText(operation: WorkspaceLifecycleOperationView, name: string): string {
  if (operation.state === "completed") return portalText("core", "workspaces._is_archived_its_history_and_invoices_are_kept_cc8e7d", { value0: name });
  return portalText("core", "account_admin.draining_no_new_calls_or_campaign_attempts_the_workspace_archives_7a3d35");
}

/** The three-column table. */
export function ArchivePreflightTable(props: { readonly item: readonly ArchivePreflightItemView[] }): ReactNode {
  usePortalTranslation("core");
  return (
    <div className="op-table-wrap">
      <table className="table op-table ap-table">
        <thead>
          <tr>
            <th>
              <span className="wt-visually-hidden">{portalText("core", "workspaces.item_ecdda5")}</span>
            </th>
            <th>{portalText("core", "workspaces.now_e3b820")}</th>
            <th>{portalText("core", "workspaces.on_archive_80f498")}</th>
          </tr>
        </thead>
        <tbody>
          {props.item.map((one) => (
            <tr key={one.item}>
              <td className="ap-item">{PREFLIGHT_ITEM_WORD[one.item]}</td>
              <td className="op-cell ap-now">{one.now}</td>
              <td className="ap-effect">{one.onArchive}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The confirmation dialog: the preflight, then "Archive when calls finish". `onDone` is told what happened, in words.
 * The Idempotency-Key is minted once per opening, so a retried confirmation never starts a second archive.
 */
export function ArchivePreflightDialog(props: {
  readonly open: boolean;
  readonly workspaceId: string;
  readonly onClose: () => void;
  readonly onDone: (text: PortalMessage) => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [key] = useState(() => crypto.randomUUID());
  const [problem, setProblem] = usePortalMessage();
  const preflight = useQuery({
    queryKey: archivePreflightKey(props.workspaceId),
    queryFn: () => gateway.readArchivePreflight(props.workspaceId),
    enabled: props.open,
    retry: false,
  });
  const archive = useMutation({
    mutationFn: () => gateway.archiveWorkspaceWhenIdle(props.workspaceId, key),
    onSuccess: async (operation) => {
      await queryClient.invalidateQueries({ queryKey: ["workspace-lifecycle"] });
      await queryClient.invalidateQueries({ queryKey: ["workspace"] });
      props.onDone(() => (archiveOutcomeText(operation, preflight.data?.workspace.name ?? portalText("core", "workspaces.the_workspace_270f31"))));
      props.onClose();
    },
    onError: (raised: unknown) => {
      setProblem(() => (apiProblemText(raised, portalText("core", "workspaces.that_workspace_could_not_be_archived_b6d6f9"))));
    },
  });
  const name = preflight.data?.workspace.name ?? portalText("core", "workspaces.this_workspace_5ed971");
  const draining = preflight.data?.openOperation !== null && preflight.data?.openOperation !== undefined;
  return (
    <Dialog
      open={props.open}
      title={portalText("core", "account_admin.before_archiving_c10e91", { value0: name })}
      onClose={props.onClose}
      actions={
        <>
          <Button variant="secondary" onClick={props.onClose}>{portalText("core", "workspaces.keep_it_d9f369")}</Button>
          <Button
            variant="primary"
            disabled={preflight.data === undefined || draining || preflight.data.workspace.state !== "active" || archive.isPending}
            onClick={() => {
              setProblem(null);
              archive.mutate();
            }}
          >
            {archive.isPending ? portalText("core", "workspaces.archiving_b5a3d7") : portalText("core", "account_admin.archive_when_calls_finish_156f6a")}
          </Button>
        </>
      }
    >
      <p className="c-note">{portalText("core", "workspaces.what_will_happen_item_by_item_nothing_is_archived_until_you_confi_96b95b")}</p>
      {preflight.isPending ? <p className="c-note">{portalText("core", "workspaces.checking_what_this_workspace_has_running_835956")}</p> : null}
      {preflight.isError ? (
        <p className="c-note c-note-critical" role="alert">
          {apiProblemText(preflight.error, portalText("core", "account_admin.what_archiving_would_do_could_not_be_read_04da19"))}
        </p>
      ) : null}
      {preflight.data === undefined ? null : <ArchivePreflightTable item={preflight.data.item} />}
      {draining ? <p className="c-note">{portalText("core", "workspaces.this_workspace_is_already_draining_it_archives_when_its_last_conv_40ae7a")}</p> : null}
      {problem === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {problem}
        </p>
      )}
    </Dialog>
  );
}
